import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { animate } from "motion";
import { LayoutDashboard, Settings, Users, Building2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthProvider';

const NavLinks = ({ isCollapsed = false, forceCloseDropdowns = false, isMobile = false, activeDropdown = null, onDropdownClick = null }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { hasRole, can } = useAuth();

  // Check if user has admin or director role
  const hasAdminOrDirectorRole = () => {
    return hasRole('ROLE_ADMIN') || hasRole('ROLE_DIRECTOR') || hasRole('admin') || hasRole('director');
  };

  // Helper function to check if user has any view permission for a module
  // (some modules use scoped keys like view_own/view_department/view_global,
  // others use a flat 'view' key)
  const canViewModule = (moduleName) => {
    return can('view_own', moduleName) ||
           can('view_department', moduleName) ||
           can('view_global', moduleName) ||
           can('view', moduleName);
  };

  // Path arrays for each dropdown group
  const dashboardPaths = [
    '/dashboard'
  ];
  const BusinessPartnerPaths = [
    '/masterdata-company',
    '/masterdata-contacts',
  ];
  const IndividualPartnerPaths = [
    '/masterdata-individual-partner',
  ];
  const solutionSetupPaths = [
    '/masterdata-services',
    '/masterdata-scheme-category',
    '/masterdata-audittype',
    '/masterdata-standard',
    '/masterdata-activity-list',
    '/masterdata-commercial-terms',
    //'/masterdata-business-process'
  ];
  const auditPaths = [
    '/masterdata-personnel-qualification',
    '/masterdata-audit-competency',
    '/masterdata-audit-impartiality',
    '/masterdata-audit-workflow'
  ];
  const configurationPaths = [
    '/masterdata-isccscope',
    '/masterdata-isccmaterialcategory',
    '/masterdata-isccmaterial',
    '/masterdata-source',
    '/masterdata-lead-status',
    '/masterdata-proposal-status',
    '/masterdata-proposal-stage',
    '/masterdata-reason-loss',
    '/masterdata-tagging',
    '/masterdata-competitor',
    '/masterdata-status',
    '/masterdata-task-type',
    '/masterdata-entity-type',
    '/masterdata-business-partner-type',
    '/masterdata-individual-partner-type',
    '/masterdata-sector',
    '/masterdata-nacecode',
    '/masterdata-lvvscheme',
    '/masterdata-lvvnek',
    '/masterdata-product-level',
    '/masterdata-product-category',
  ];
  const businessSettingsPaths = [
    '/masterdata-departement',
    '/masterdata-jobtitle',
    '/masterdata-branch'
  ];
  const usersPaths = [
    '/masterdata-list-users'
  ];
  const masterDataPaths = [
    ...BusinessPartnerPaths,
    ...IndividualPartnerPaths,
    ...solutionSetupPaths,
    ...configurationPaths,
    ...auditPaths,
  ];

  // State for tracking which dropdowns are open
  const [openDropdowns, setOpenDropdowns] = useState({
    masterData: false,
    businessPartner: false,
    individualPartner: false,
    solutionSetup: false,
    configuration: false,
    audit: false,
    businessSettings: false,
    users: false
  });

  // Helper to check if current path is in a group
  const isPathInCategory = (pathsArray) => {
    return pathsArray.some(path => currentPath === path);
  };

  // Effect to handle forced dropdown closure
  useEffect(() => {
    if (forceCloseDropdowns) {
      setOpenDropdowns({
        masterData: false,
        businessPartner: false,
        individualPartner: false,
        solutionSetup: false,
        configuration: false,
        audit: false,
        businessSettings: false,
        users: false
      });
    }
  }, [forceCloseDropdowns]);

  // Effect to handle active path dropdowns
  useEffect(() => {
    if (!forceCloseDropdowns) {
    setOpenDropdowns({
      masterData: isPathInCategory(masterDataPaths),
      businessPartner: isPathInCategory(BusinessPartnerPaths),
      individualPartner: isPathInCategory(IndividualPartnerPaths),
      solutionSetup: isPathInCategory(solutionSetupPaths),
      configuration: isPathInCategory(configurationPaths),
      audit: isPathInCategory(auditPaths),
      businessSettings: isPathInCategory(businessSettingsPaths),
      users: isPathInCategory(usersPaths)
    });
    }
  }, [currentPath, forceCloseDropdowns]);

  // Toggle dropdown state
  const toggleDropdown = (dropdown, event) => {
    // Prevent event from bubbling up
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Only toggle if not in collapsed state
    if (!isCollapsed) {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
    }
  };

  // Animate dropdown height
  const animateDropdown = (element, isOpen) => {
    if (element) {
      if (isOpen && !isCollapsed) {
        element.style.display = 'block';
        animate(element,
          {
            opacity: 1,
            height: 'auto'
          },
          {
            duration: 0.2,
            easing: "ease-out"
          }
        );
      } else {
        animate(element,
          {
            opacity: 0,
            height: 0
          },
          {
            duration: 0.2,
            easing: "ease-in",
            onComplete: () => {
              element.style.display = 'none';
            }
          }
        );
      }
    }
  };

  const navClasses = `navbar-nav mx-2 pt-lg-3 ${isCollapsed ? 'navbar-collapsed' : ''}`;

  return (
    <ul className={navClasses}>
      <li className="nav-item mb-2">
        <Link className={`nav-link rounded-3 ${isPathInCategory(dashboardPaths) ? 'active' : ''}`} to="/dashboard">
          <span className="nav-link-icon d-md-none d-lg-inline-block">
            <LayoutDashboard className="icon icon-tabler icons-tabler-outline icon-tabler-layout-dashboard" />
          </span>
          <span className="nav-link-title">
            Dashboard
          </span>
        </Link>
      </li>

      {/* Master Data - Show if user has view permission for anything nested inside this group */}
      {(canViewModule('companies') ||
        canViewModule('contacts') ||
        canViewModule('individualPartner') ||
        canViewModule('personnelQualification') ||
        canViewModule('competency') ||
        canViewModule('services') ||
        canViewModule('schemeCategory') ||
        canViewModule('activityList') ||
        canViewModule('auditType') ||
        canViewModule('standard') ||
        canViewModule('commercialTerms') ||
        canViewModule('source') ||
        canViewModule('leadsStatus') ||
        canViewModule('proposalStatus') ||
        canViewModule('reasonLoss') ||
        canViewModule('tagging') ||
        canViewModule('competitor') ||
        canViewModule('status') ||
        canViewModule('taskType') ||
        canViewModule('entityType') ||
        canViewModule('businessPartnerType') ||
        canViewModule('individualPartnerType') ||
        canViewModule('sector') ||
        canViewModule('naceCode') ||
        canViewModule('isccScope') ||
        canViewModule('isccMaterialCategory') ||
        canViewModule('isccMaterial') ||
        canViewModule('lvvScheme') ||
        canViewModule('lvvNek') ||
        canViewModule('productLevel') ||
        canViewModule('productCategory') ||
        hasAdminOrDirectorRole()) && (
        <li className={`nav-item mb-2 dropdown ${openDropdowns.masterData && !isCollapsed ? 'show' : ''}`}>
          <a
            className={`nav-link dropdown-toggle rounded-3 ${isPathInCategory(masterDataPaths) ? 'active' : ''}`}
            href="#"
            onClick={(e) => toggleDropdown('masterData', e)}
            data-bs-toggle="dropdown"
            role="button"
            aria-expanded={openDropdowns.masterData && !isCollapsed}
          >
            <span className="nav-link-icon d-md-none d-lg-inline-block">
              <Settings className="icon icon-tabler icons-tabler-outline icon-tabler-settings" />
            </span>
            <span className="nav-link-title">
              Master Data
            </span>
          </a>
          <div
            className={`dropdown-menu ${openDropdowns.masterData && !isCollapsed ? 'show' : ''}`}
            ref={el => el && animateDropdown(el, openDropdowns.masterData && !isCollapsed)}
          > 
            {/* Business Partner - only show if at least one child item is viewable */}
            {(canViewModule('companies') || canViewModule('contacts')) && (
            <div className={`dropend ${openDropdowns.businessPartner && !isCollapsed ? 'show' : ''}`}>
              <a
                className={`dropdown-item dropdown-toggle rounded-3 mb-2 mt-2 ${isPathInCategory(BusinessPartnerPaths) ? 'active' : ''}`}
                href="#"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                aria-expanded={openDropdowns.businessPartner && !isCollapsed}
                onClick={e => toggleDropdown('businessPartner', e)}
              >
                Business Partner
              </a>
              <div className={`dropdown-menu ${openDropdowns.businessPartner && !isCollapsed ? 'show' : ''}`}>
                {canViewModule('companies') && (
                  <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-company' ? 'active' : ''}`} to="/masterdata-company">
                    Companies
                  </Link>
                )}
                {canViewModule('contacts') && (
                  <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-contacts' ? 'active' : ''}`} to="/masterdata-contacts">
                    Contacts
                  </Link>
                )}
              </div>
            </div>
            )}
            {/* Individual Partner as a direct link */}
            {canViewModule('individualPartner') && (
              <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-individual-partner' ? 'active' : ''}`} to="/masterdata-individual-partner">
                Individual Partner
              </Link>
            )}
          {/* Solution Setup Group - shown if admin/director, or if user has view access to any submodule */}
          {(hasAdminOrDirectorRole() ||
            canViewModule('services') ||
            canViewModule('schemeCategory') ||
            canViewModule('activityList') ||
            canViewModule('auditType') ||
            canViewModule('standard') ||
            canViewModule('commercialTerms')) && (
            <div className={`dropend ${openDropdowns.solutionSetup && !isCollapsed ? 'show' : ''}`}>
              <a
                className={`dropdown-item dropdown-toggle rounded-3 mb-2 mt-2 ${isPathInCategory(solutionSetupPaths) ? 'active' : ''}`}
                href="#"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                aria-expanded={openDropdowns.solutionSetup && !isCollapsed}
                onClick={e => toggleDropdown('solutionSetup', e)}
              >
              Solution Setup
            </a>
              <div className={`dropdown-menu ${openDropdowns.solutionSetup && !isCollapsed ? 'show' : ''}`}>
                {(hasAdminOrDirectorRole() || canViewModule('services')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-services' ? 'active' : ''}`} to="/masterdata-services">
                Services
              </Link>
                )}
                {(hasAdminOrDirectorRole() || canViewModule('schemeCategory')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-scheme-category' ? 'active' : ''}`} to="/masterdata-scheme-category">
                Scheme Category
              </Link>
                )}
                {(hasAdminOrDirectorRole() || canViewModule('activityList')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-activity-list' ? 'active' : ''}`} to="/masterdata-activity-list">
                Activity List
              </Link>
                )}
                {/* <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-business-process' ? 'active' : ''}`} to="/masterdata-business-process">
                Business Process
              </Link> */}
                {(hasAdminOrDirectorRole() || canViewModule('auditType')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-audittype' ? 'active' : ''}`} to="/masterdata-audittype">
                Audit Type / Service Item
              </Link>
                )}
                {(hasAdminOrDirectorRole() || canViewModule('standard')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-standard' ? 'active' : ''}`} to="/masterdata-standard">
                Standard
              </Link>
                )}
                {(hasAdminOrDirectorRole() || canViewModule('commercialTerms')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-commercial-terms' ? 'active' : ''}`} to="/masterdata-commercial-terms">
                Commercial Terms
              </Link>
                )}
            </div>
          </div>
          )}
          {/* Audit Group - shown if admin/director, or if user has view access to any audit submodule */}
          {(hasAdminOrDirectorRole() || canViewModule('personnelQualification') || canViewModule('competency')) && (
            <div className={`dropend ${openDropdowns.audit && !isCollapsed ? 'show' : ''}`}>
              <a
                className={`dropdown-item dropdown-toggle rounded-3 mb-2 mt-2 ${isPathInCategory(auditPaths) ? 'active' : ''}`}
                href="#"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                aria-expanded={openDropdowns.audit && !isCollapsed}
                onClick={e => toggleDropdown('audit', e)}
              >
                Audit
              </a>
              <div className={`dropdown-menu ${openDropdowns.audit && !isCollapsed ? 'show' : ''}`}>
                {(hasAdminOrDirectorRole() || canViewModule('personnelQualification')) && (
                  <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-personnel-qualification' ? 'active' : ''}`} to="/masterdata-personnel-qualification">
                    Personnel Qualification
                  </Link>
                )}
                {(hasAdminOrDirectorRole() || canViewModule('competency')) && (
                  <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-audit-competency' ? 'active' : ''}`} to="/masterdata-audit-competency">
                    Competency
                  </Link>
                )}
                {/* Impartiality and Workflow menu items hidden per request (2026-07-21) — routes still exist, just not linked from this menu. */}
              </div>
            </div>
          )}
          {/* Configuration - shown if user has view access to any submodule */}
          {(canViewModule('source') ||
            canViewModule('leadsStatus') ||
            canViewModule('proposalStatus') ||
            canViewModule('reasonLoss') ||
            canViewModule('tagging') ||
            canViewModule('competitor') ||
            canViewModule('status') ||
            canViewModule('taskType') ||
            canViewModule('entityType') ||
            canViewModule('businessPartnerType') ||
            canViewModule('individualPartnerType') ||
            canViewModule('sector') ||
            canViewModule('naceCode') ||
            canViewModule('isccScope') ||
            canViewModule('isccMaterialCategory') ||
            canViewModule('isccMaterial') ||
            canViewModule('lvvScheme') ||
            canViewModule('lvvNek') ||
            canViewModule('productLevel') ||
            canViewModule('productCategory')) && (
            <div className={`dropend ${openDropdowns.configuration && !isCollapsed ? 'show' : ''}`}>
              <a
                className={`dropdown-item dropdown-toggle rounded-3 mb-2 mt-2 ${isPathInCategory(configurationPaths) ? 'active' : ''}`}
                href="#"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                aria-expanded={openDropdowns.configuration && !isCollapsed}
                onClick={e => toggleDropdown('configuration', e)}
              >
              Configuration
            </a>
              <div className={`dropdown-menu ${openDropdowns.configuration && !isCollapsed ? 'show' : ''}`}>
                {(canViewModule('source')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-source' ? 'active' : ''}`} to="/masterdata-source">
                Lead Source
              </Link>
                )}
                {(canViewModule('leadsStatus')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-lead-status' ? 'active' : ''}`} to="/masterdata-lead-status">
                Lead Status
              </Link>
                )}
                {(canViewModule('proposalStatus')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-proposal-status' ? 'active' : ''}`} to="/masterdata-proposal-status">
                Proposal Status
              </Link>
                )}
                {(canViewModule('proposalStage')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-proposal-stage' ? 'active' : ''}`} to="/masterdata-proposal-stage">
                Proposal Stage
              </Link>
                )}
                {(canViewModule('reasonLoss')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-reason-loss' ? 'active' : ''}`} to="/masterdata-reason-loss">
                Reason Loss
              </Link>
                )}
                {(canViewModule('tagging')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-tagging' ? 'active' : ''}`} to="/masterdata-tagging">
                Tagging
              </Link>
                )}
                {(canViewModule('competitor')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-competitor' ? 'active' : ''}`} to="/masterdata-competitor">
                Competitor
              </Link>
                )}
                {(canViewModule('status')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-status' ? 'active' : ''}`} to="/masterdata-status">
                General Status
              </Link>
                )}
                {(canViewModule('taskType')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-task-type' ? 'active' : ''}`} to="/masterdata-task-type">
                Task Type
              </Link>
                )}
                {(canViewModule('entityType')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-entity-type' ? 'active' : ''}`} to="/masterdata-entity-type">
                Entity Type
              </Link>
                )}
                {(canViewModule('businessPartnerType')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-business-partner-type' ? 'active' : ''}`} to="/masterdata-business-partner-type">
                Business Partner Type
              </Link>
                )}
                {(canViewModule('individualPartnerType')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-individual-partner-type' ? 'active' : ''}`} to="/masterdata-individual-partner-type">
                Individual Partner Type
              </Link>
                )}
                {(canViewModule('sector')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-sector' ? 'active' : ''}`} to="/masterdata-sector">
                Sector
              </Link>
                )}
                {(canViewModule('naceCode')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-nacecode' ? 'active' : ''}`} to="/masterdata-nacecode">
                NACE Code
              </Link>
                )}
                {(canViewModule('isccScope')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-isccscope' ? 'active' : ''}`} to="/masterdata-isccscope">
                ISCC Scope
              </Link>
                )}
                {(canViewModule('isccMaterialCategory')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-isccmaterialcategory' ? 'active' : ''}`} to="/masterdata-isccmaterialcategory">
                ISCC Material Category
              </Link>
                )}
                {(canViewModule('isccMaterial')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-isccmaterial' ? 'active' : ''}`} to="/masterdata-isccmaterial">
                ISCC Material
              </Link>
                )}
                {(canViewModule('lvvScheme')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-lvvscheme' ? 'active' : ''}`} to="/masterdata-lvvscheme">
                LVV Scheme
              </Link>
                )}
                {(canViewModule('lvvNek')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-lvvnek' ? 'active' : ''}`} to="/masterdata-lvvnek">
                LVV NEK
              </Link>
                )}
                {(canViewModule('productLevel')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-product-level' ? 'active' : ''}`} to="/masterdata-product-level">
                Product Level
              </Link>
                )}
                {(canViewModule('productCategory')) && (
                <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-product-category' ? 'active' : ''}`} to="/masterdata-product-category">
                Product Category
              </Link>
                )}
            </div>
          </div>
          )}
          </div>
        </li>
      )}

      {/* Business Settings - shown if admin/director, or if user has view access to any submodule */}
      {(hasAdminOrDirectorRole() ||
        canViewModule('department') ||
        canViewModule('jobTitle') ||
        canViewModule('branches')) && (
        <li className={`nav-item mb-2 dropdown ${openDropdowns.businessSettings && !isCollapsed ? 'show' : ''}`}>
          <a
            className={`nav-link dropdown-toggle rounded-3 ${isPathInCategory(businessSettingsPaths) ? 'active' : ''}`}
            href="#"
            onClick={(e) => toggleDropdown('businessSettings', e)}
          data-bs-toggle="dropdown"
          role="button"
            aria-expanded={openDropdowns.businessSettings && !isCollapsed}
        >
          <span className="nav-link-icon d-md-none d-lg-inline-block">
              <Building2 className="icon icon-tabler icons-tabler-outline icon-tabler-building" />
            </span>
            <span className="nav-link-title">
              Business Settings
          </span>
          </a>
          <div
            className={`dropdown-menu ${openDropdowns.businessSettings && !isCollapsed ? 'show' : ''}`}
            ref={el => el && animateDropdown(el, openDropdowns.businessSettings && !isCollapsed)}
          >
            {(hasAdminOrDirectorRole() || canViewModule('department')) && (
            <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-departement' ? 'active' : ''}`} to="/masterdata-departement">
            Departement
          </Link>
            )}
            {(hasAdminOrDirectorRole() || canViewModule('jobTitle')) && (
            <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-jobtitle' ? 'active' : ''}`} to="/masterdata-jobtitle">
            Job Title
          </Link>
            )}
            {(hasAdminOrDirectorRole() || canViewModule('branches')) && (
            <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-branch' ? 'active' : ''}`} to="/masterdata-branch">
            Branch
          </Link>
            )}
        </div>
      </li>
      )}

      {/* Users - shown if admin/director, or if user has view access to listUsers */}
      {(hasAdminOrDirectorRole() || canViewModule('listUsers')) && (
        <li className={`nav-item mb-2 dropdown ${openDropdowns.users && !isCollapsed ? 'show' : ''}`}>
          <a
            className={`nav-link dropdown-toggle rounded-3 ${isPathInCategory(usersPaths) ? 'active' : ''}`}
            href="#"
            onClick={(e) => toggleDropdown('users', e)}
          data-bs-toggle="dropdown"
          role="button"
            aria-expanded={openDropdowns.users && !isCollapsed}
        >
          <span className="nav-link-icon d-md-none d-lg-inline-block">
              <Users className="icon icon-tabler icons-tabler-outline icon-tabler-user" />
            </span>
            <span className="nav-link-title">
              Users
          </span>
          </a>
          <div
            className={`dropdown-menu ${openDropdowns.users && !isCollapsed ? 'show' : ''}`}
            ref={el => el && animateDropdown(el, openDropdowns.users && !isCollapsed)}
          >
            {(hasAdminOrDirectorRole() || canViewModule('listUsers')) && <Link className={`dropdown-item rounded-3 mb-2 mt-2 ${currentPath === '/masterdata-list-users' ? 'active' : ''}`} to="/masterdata-list-users">List Users</Link>}
        </div>
      </li>
      )}
    </ul>
  );
};

export default NavLinks; 